import { ConfirmationToken as StripeConfirmationToken } from '@stripe/stripe-js';

import { notify } from 'src/clients/ApiService';
import { AppAPIName, UnAuthAPIName } from 'src/constants';
import { recursiveLoad } from 'src/services/recursiveLoad';
import { RootState } from 'src/store';
import { SubscriptionInterval } from 'src/types/subscriptions';
import { API } from 'src/utils/AmplifyApiUtils';
import { IsLoggedIn } from 'src/utils/AuthUtils';
import { ensureApiError } from 'src/utils/Errors';

import {
  formatLineItems,
  Invoice,
  LineItem as InvoiceLineItem,
  PaymentPreferences,
  updateInvoiceQuery,
} from './invoicesApi';

import { ApiMethods, ApiTags, appAPI } from '.';

export type LineItem = InvoiceLineItem & {
  rate?: number;
  priceInterval?: SubscriptionInterval;
};

export enum PaymentLinkStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

export interface PaymentLink {
  id: string;
  lineItems: LineItem[];
  coverUrl: string;
  description: string;
  paymentPreferences: PaymentPreferences;
  status: PaymentLinkStatus;
  name: string;
  createdAt: string;
  updatedAt: string;
  currency: string;
  ref: string;
  interval: string;
}

export interface CreatePaymentLinkInput {
  lineItems: LineItem[];
  coverUrl: string;
  description: string;
  paymentPreferences: PaymentPreferences;
  status: PaymentLinkStatus;
  name: string;
  currency: string;
}

export interface UpdatePaymentLinkInput
  extends Partial<CreatePaymentLinkInput> {
  id: string;
}

export type PayPaymentLinkInput = {
  id: string;
  sourceId?: string;
  confirmationToken?: StripeConfirmationToken;
  name?: string;
  email?: string;
  company?: string;
};

export type PayPaymentLinkResponse = {
  redirectUrl: string;
  invoice: Invoice;
  magicLinkSent: boolean;
};

const formattedPaymentLinks = (paymentLink: PaymentLink[]) => {
  return paymentLink.sort((a, b) => {
    if (
      a.status === PaymentLinkStatus.Active &&
      b.status !== PaymentLinkStatus.Active
    )
      return -1;
    if (
      a.status !== PaymentLinkStatus.Active &&
      b.status === PaymentLinkStatus.Active
    )
      return 1;

    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
};

export const paymentLinksApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getPaymentLink: build.query<PaymentLink, string>({
      queryFn: async (id) => {
        const isLoggedInUser = await IsLoggedIn();
        try {
          const data = await API.get(
            isLoggedInUser ? AppAPIName : UnAuthAPIName,
            `/${
              isLoggedInUser ? 'payment-links' : 'public/paymentlinks'
            }/${id}`,
            {},
          );
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: [ApiTags.payment_links],
    }),

    listPaymentLinks: build.query<PaymentLink[], void>({
      queryFn: () => {
        return recursiveLoad<PaymentLink>(
          (currentQueryOptions) =>
            API.get(AppAPIName, '/payment-links', currentQueryOptions),
          {},
        ).then((response) => {
          return {
            data: formattedPaymentLinks(response.data),
          };
        });
      },
      providesTags: [ApiTags.payment_links],
    }),

    createPaymentLink: build.mutation<PaymentLink, CreatePaymentLinkInput>({
      query: (paymentLink) => ({
        path: '/payment-links',
        method: ApiMethods.post,
        options: {
          body: {
            ...paymentLink,
            status: paymentLink.status,
            lineItems: formatLineItems(paymentLink.lineItems),
          },
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          // Create a temporary optimistic payment link
          const result = await queryFulfilled;
          const paymentLink = result.data;
          dispatch(
            paymentLinksApi.util.updateQueryData(
              'listPaymentLinks',
              undefined,
              (draft) => {
                draft.unshift(paymentLink);
              },
            ),
          );
        } catch (err) {
          const error = ensureApiError(err);
          const errorMessage = error.message;
          notify({
            status: 'error',
            errorMessage: errorMessage || 'Could not create payment link',
            dispatch,
          });
        }
      },
    }),

    updatePaymentLink: build.mutation<PaymentLink, UpdatePaymentLinkInput>({
      query: ({ id, ...updatePaymentLink }) => ({
        path: `/payment-links/${id}`,
        method: ApiMethods.put,
        options: {
          body: {
            ...updatePaymentLink,
            lineItems: formatLineItems(updatePaymentLink.lineItems || []),
          },
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        // Optimistically update the payment link in the list
        const result = await queryFulfilled;
        const updatePaymentLink = result.data;

        const patchResult = dispatch(
          paymentLinksApi.util.updateQueryData(
            'listPaymentLinks',
            undefined,
            (draft) => {
              const paymentLink = draft.find(
                (link) => link.id === updatePaymentLink.id,
              );
              if (paymentLink) {
                Object.assign(paymentLink, updatePaymentLink);
              }
            },
          ),
        );

        // Also update the individual payment link query if it exists
        const detailsPatchResult = dispatch(
          paymentLinksApi.util.updateQueryData(
            'getPaymentLink',
            updatePaymentLink.id,
            (draft) => {
              Object.assign(draft, updatePaymentLink);
            },
          ),
        );

        try {
          await queryFulfilled;
          notify({
            status: 'success',
            successMessage: 'Payment link updated successfully',
            dispatch,
          });
        } catch (err) {
          // If the mutation fails, revert both optimistic updates
          patchResult.undo();
          detailsPatchResult.undo();
          const error = ensureApiError(err);
          const errorMessage = error.message;
          notify({
            status: 'error',
            errorMessage: errorMessage ?? 'Failed to update payment link',
            error,
            dispatch,
          });
        }
      },
    }),

    activatePaymentLink: build.mutation<PaymentLink, UpdatePaymentLinkInput>({
      query: (data) => ({
        path: `/payment-links/${data.id}`,
        method: ApiMethods.put,
        options: {
          body: {
            ...data,
            status: PaymentLinkStatus.Active,
          },
        },
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        // Optimistically update the payment link status in the list
        const patchResult = dispatch(
          paymentLinksApi.util.updateQueryData(
            'listPaymentLinks',
            undefined,
            (draft) => {
              const paymentLink = draft.find((link) => link.id === id);
              if (paymentLink) {
                paymentLink.status = PaymentLinkStatus.Active;
              }
              return formattedPaymentLinks(draft);
            },
          ),
        );

        try {
          await queryFulfilled;
          notify({
            status: 'success',
            successMessage: 'Payment link activated successfully',
            dispatch,
          });
        } catch (error) {
          // If the mutation fails, revert the optimistic update
          patchResult.undo();
          notify({
            status: 'error',
            errorMessage: 'Failed to activate payment link',
            error,
            dispatch,
          });
        }
      },
    }),

    deactivatePaymentLink: build.mutation<PaymentLink, UpdatePaymentLinkInput>({
      query: (data) => ({
        path: `/payment-links/${data.id}`,
        method: ApiMethods.put,
        options: {
          body: {
            ...data,
            status: PaymentLinkStatus.Inactive,
          },
        },
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        // Optimistically update the payment link status in the list
        const patchResult = dispatch(
          paymentLinksApi.util.updateQueryData(
            'listPaymentLinks',
            undefined,
            (draft) => {
              const paymentLink = draft.find((link) => link.id === id);
              if (paymentLink) {
                paymentLink.status = PaymentLinkStatus.Inactive;
              }
              return formattedPaymentLinks(draft);
            },
          ),
        );

        try {
          await queryFulfilled;
          notify({
            status: 'success',
            successMessage: 'Payment link deactivated successfully',
            dispatch,
          });
        } catch (error) {
          // If the mutation fails, revert the optimistic update
          patchResult.undo();
          notify({
            status: 'error',
            errorMessage: 'Failed to deactivate payment link',
            error,
            dispatch,
          });
        }
      },
    }),
    payPaymentLink: build.mutation<PayPaymentLinkResponse, PayPaymentLinkInput>(
      {
        queryFn: async (payLinkInput, { dispatch }) => {
          const isLoggedInUser = await IsLoggedIn();
          try {
            const data = await API.post(
              isLoggedInUser ? AppAPIName : UnAuthAPIName,
              `/${isLoggedInUser ? 'payment-links' : 'public/paymentlinks'}/${
                payLinkInput.id
              }/pay`,
              {
                body: {
                  paymentLinkId: payLinkInput.id,
                  sourceId: payLinkInput.sourceId,
                  confirmationToken: payLinkInput.confirmationToken,
                  ...(payLinkInput.name && { name: payLinkInput.name }),
                  ...(payLinkInput.email && { email: payLinkInput.email }),
                  ...(payLinkInput.company && {
                    company: payLinkInput.company,
                  }),
                },
              },
            );

            return { data };
          } catch (err) {
            const error = ensureApiError(err);
            notify({
              status: 'error',
              errorMessage: error.message || 'Failed to pay payment link',
              dispatch,
            });
            return { error };
          }
        },

        async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
          const { data } = await queryFulfilled;
          if (data && typeof data === 'object') {
            const state = getState() as unknown as RootState;
            updateInvoiceQuery(data.invoice, dispatch, state);
          }
        },
      },
    ),
  }),
});

export const {
  useGetPaymentLinkQuery,
  useListPaymentLinksQuery,
  useCreatePaymentLinkMutation,
  useActivatePaymentLinkMutation,
  useDeactivatePaymentLinkMutation,
  useUpdatePaymentLinkMutation,
  usePayPaymentLinkMutation,
} = paymentLinksApi;
